import { IEConsentForm } from "@/api/dtos";
import EConsentContext, { EConsentUserType, IEConsentContext } from "@/contexts/EConsentContext";
import usePatientEConsentForm from "@/hooks/data/usePatientEConsentForm";
import {
    FormContext,
    FormCreateContext,
    FormDefinitionContext,
    InstitutionContext,
    IFormContext
} from "@ngt/opms";
import { ComponentType, FC, PropsWithChildren, useMemo } from "react";

export interface IEConsentByGuidProviderProps extends PropsWithChildren {
    guid?: string;
    formDefinitionIdentifier?: string | number;
    loadBeforeMount?: boolean;
    LoadingComponent?: ComponentType<any>;
}

const EConsentByGuidProvider: FC<IEConsentByGuidProviderProps> = ({
    guid,
    formDefinitionIdentifier,
    loadBeforeMount,
    LoadingComponent,
    children
}) => {
    const formCode = typeof formDefinitionIdentifier === "string" ? formDefinitionIdentifier : undefined;
    const query = usePatientEConsentForm<IEConsentForm>(formCode, guid);

    const form = useMemo(() => {
        if (!query.data?.form) return undefined;

        return query.data.form;
    }, [query.data]);

    const formContext = useMemo<IFormContext<IEConsentForm>>(() => ({
        data: form,
        error: query.error as any,
        loading: query.loading,
        validating: query.isValidating,
        mutate: async () => {
            const refreshed = await query.mutate();
            return refreshed ? { form: refreshed.form } as any : undefined;
        },
        refresh: async () => (await query.mutate())?.form
    }), [form, query.error, query.loading, query.isValidating, query.mutate]);

    const formDefinitionContext = useMemo(() => ({
        data: {
            id: query.registration?.formDefinitionId,
            code: query.data?.context?.formDefinitionCode ?? formCode,
            name: query.data?.context?.formDefinitionName
        },
        loading: query.loading
    }), [formCode, query.data?.context, query.loading, query.registration]);

    const institutionContext = useMemo(() => ({
        data: {
            code: query.data?.context?.institutionCode,
            name: query.data?.context?.institutionName
        },
        loading: query.loading
    }), [query.data?.context, query.loading]);

    const eConsentContext = useMemo<IEConsentContext>(() => ({
        investigators: null,
        principalInvestigator: query.data?.context?.principalInvestigator,
        userType: EConsentUserType.Participant,
        participantAccessGuid: guid,
        participantName: query.data?.context?.participantName,
        picfDownloadUrl: query.data?.context?.picfDownloadUrl,
        picfVersion: query.data?.context?.picfVersion,
        readOnly: query.data?.context?.readOnly,
        consentStatus: query.data?.context?.consentStatus
    }), [form, guid, query.data?.context]);

    const formCreateContext = useMemo(() => ({
        createPatient: false,
        createEvent: false,
        createForm: false
    }), []);

    if (loadBeforeMount && query.loading) {
        return LoadingComponent ? <LoadingComponent /> : <>Loading</>;
    }

    return (
        <FormDefinitionContext.Provider value={formDefinitionContext as any}>
            <InstitutionContext.Provider value={institutionContext as any}>
                <FormCreateContext.Provider value={formCreateContext}>
                    <FormContext.Provider value={formContext as any}>
                        <EConsentContext.Provider value={eConsentContext}>
                            {children}
                        </EConsentContext.Provider>
                    </FormContext.Provider>
                </FormCreateContext.Provider>
            </InstitutionContext.Provider>
        </FormDefinitionContext.Provider>
    );
};

export default EConsentByGuidProvider;
